import { useEffect, useState } from "react";
import useMaster from "hook/useMaster";
import { useForm } from "@mantine/form";

import { initialFormValues } from "initialStates";
import { checkPAN, showToast } from "utils/utility";
import { useApi } from "libs/data-access/src/api/useApi";
import { NewAffiliateStep1Validation, NewAffiliateStep2Validation } from "Pages/Affiliates/hook/affiliateValidator";

interface IProps {
    onSave: () => void;
    onCancel: () => void;
};

export default function useAffiliateOnboardRequest({
    onSave,
    onCancel
}: IProps) {

    const {
        postData
    } = useApi();
    const {
        countryList,
        getCountryList,
        allStateList,
        getAllStateListByCountryId,
        allCompanyTypes,
        getAllCompanyTypes,
        stateList,
        getStateListBasedOnCountry
    } = useMaster();

    const COMPANY_TYPE = ["PROPRIETORSHIP", "PARTNERSHIP_COMPANY", "INDIVIDUAL"];

    const [formStep, setFormStep] = useState<number>(1);
    const [isAgreeWithTerm, setIsAgreeWithTerm] = useState<boolean>(false);
    const [validations, setValidations] = useState({});
    const [PANVerification, setPANVerification] = useState<{
        PAN: string | null;
        isValid: boolean;
        isVerifying: boolean;
    }>({
        PAN: null,
        isValid: false,
        isVerifying: false
    });
    const [isSavinngData, setIsSavingData] = useState<boolean>(false);


    useEffect(() => {
        if (formStep === 1) {
            setValidations(NewAffiliateStep1Validation)
        } else {
            setValidations({ ...NewAffiliateStep1Validation, ...NewAffiliateStep2Validation })
        };
    }, [formStep]);

    const affiliateForm = useForm({
        mode: "uncontrolled",
        initialValues: initialFormValues,
        validate: validations,
        transformValues: (values) => ({
            ...values,
            countryId: Number(values.countryId),
            stateId: Number(values.stateId),
            tdsExemptionRate: values.tdsExemptionRate ? +values.tdsExemptionRate : null,
            tdsExemptionCode: values.tdsExemptionCode ? +values.tdsExemptionCode : null,
            tdsExemptionNumber: values.tdsExemptionNumber ? +values.tdsExemptionNumber : null,
        })
    });

    useEffect(() => {
        getCountryList();
    }, []);


    // API CALLS
    const verifyPAN = async () => {

        // const formData = affiliateForm.getTransformedValues();
        // affiliateForm.setFieldValue("panNumber", PANVerification.PAN?.trim() as string);
        // affiliateForm.setFieldValue("FirstName", formData.firstName);
        // affiliateForm.setFieldValue("LastName", formData.lastName);

        // setPANVerification({
        //     PAN: PANVerification.PAN?.trim() as string,
        //     isValid: true,
        //     isVerifying: false
        // });

        // return;

        const formValues = affiliateForm.getTransformedValues();

        if (!PANVerification.PAN) {
            showToast({
                type: "warning",
                message: "Enter PAN number!"
            });
            return;
        };

        if (PANVerification.PAN.length !== 10) {
            showToast({
                type: "warning",
                message: "Enter valid PAN number!"
            });
            return;
        };

        if (!checkPAN(PANVerification.PAN)) {
            showToast({
                type: "warning",
                message: "Enter valid PAN number!"
            });
            return;
        };

        if (!formValues.firstName) {
            showToast({
                type: "warning",
                message: "Enter first name!"
            });
            return;
        };

        if (!formValues.lastName) {
            showToast({
                type: "warning",
                message: "Enter last name!"
            });
            return;
        };

        setPANVerification({ ...PANVerification, isVerifying: true });

        const payload = {
            panNumber: PANVerification.PAN?.trim(),
            nameOnCard: formValues.firstName + " " + formValues.lastName,
            dob: "06/03/1990"
        };

        const response = await postData("unsecure/pan-card/verification", payload);

        setPANVerification({ ...PANVerification, isVerifying: false });

        if (response.isSuccess) {
            // console.log("PAN validated successfully: ", response.data.data);
            const data: {
                panValid: boolean;
                nameValid: boolean;
                panNumber: string;
                panName: string;
                dob: string;
            } = response.data.data;

            if (data.panValid === false) {
                showToast({
                    type: "error",
                    time: 1000 * 8,
                    title: "PAN is not valid",
                    message: "Please provide PAN number as per PAN card"
                });
                return;
            };

            if (data.panNumber === payload.panNumber) {
                const firstName = data.panName.split(" ")[0];
                const lastName = data.panName.split(" ")[1] || "";

                affiliateForm.setFieldValue("panNumber", data.panNumber);
                affiliateForm.setFieldValue("FirstName", firstName);
                affiliateForm.setFieldValue("LastName", lastName);

                setPANVerification({
                    PAN: data.panNumber,
                    isValid: true,
                    isVerifying: false
                });

                showToast({
                    type: "success",
                    message: "PAN validated successfully!"
                });
            } else {
                showToast({
                    type: "error",
                    message: "PAN number does not match!"
                });
            };
        } else {
            console.log("Error while validating PAN: ", response);
            showToast({
                type: "error",
                title: "Error while validating PAN",
                message: response.message || "Error while validating PAN"
            });
        };

    };

    const postNewAffiliate = async () => {
        setIsSavingData(true);

        const formValues = affiliateForm.getTransformedValues();
        // console.log("Payload For Affiliate: ", formValues);

        const response = await postData(`unsecure/affiliate/request/form`, formValues);

        if (response.isSuccess) {
            showToast({
                message: "Saved Successfully",
                type: "success",
            });
            affiliateForm.reset();
            onSave();
        } else {
            console.log("Error while saving data: ", response);
            showToast({
                message: response.message,
                type: "error",
            });
            // affiliateForm.reset();
            // onCancel();
        };

        setIsSavingData(false);
    };


    return {
        COMPANY_TYPE,
        countryList,
        stateList,
        getStateListBasedOnCountry,
        formStep,
        setFormStep,
        isSavinngData,
        isAgreeWithTerm,
        setIsAgreeWithTerm,
        validations,
        PANVerification,
        setPANVerification,
        affiliateForm,
        verifyPAN,
        postNewAffiliate
    };
};